import { cn } from '@foundation/utils/cn';

import {
  getResponsiveEntries,
  resolveResponsiveProp,
  type Breakpoint,
  type Responsive,
} from '@design-system/headless-ui';

export type SpinnerVariant = 'dots' | 'ring';
export type SpinnerSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

export type SpinnerProps = {
  readonly variant?: SpinnerVariant;
  readonly size?: Responsive<SpinnerSize>;
};

const DOTS_SIZES = {
  base: {
    xsmall: 'size-4',
    small: 'size-6',
    medium: 'size-8',
    large: 'size-12',
    xlarge: 'size-16',
  },
  sm: {
    xsmall: 'sm:size-4',
    small: 'sm:size-6',
    medium: 'sm:size-8',
    large: 'sm:size-12',
    xlarge: 'sm:size-16',
  },
  md: {
    xsmall: 'md:size-4',
    small: 'md:size-6',
    medium: 'md:size-8',
    large: 'md:size-12',
    xlarge: 'md:size-16',
  },
  lg: {
    xsmall: 'lg:size-4',
    small: 'lg:size-6',
    medium: 'lg:size-8',
    large: 'lg:size-12',
    xlarge: 'lg:size-16',
  },
  xl: {
    xsmall: 'xl:size-4',
    small: 'xl:size-6',
    medium: 'xl:size-8',
    large: 'xl:size-12',
    xlarge: 'xl:size-16',
  },
} as const satisfies Record<Breakpoint, Record<SpinnerSize, string>>;

const RING_SIZES = {
  base: {
    xsmall: 'size-4 border-2',
    small: 'size-6 border-2',
    medium: 'size-8 border-[3px]',
    large: 'size-12 border-4',
    xlarge: 'size-16 border-4',
  },
  sm: {
    xsmall: 'sm:size-4 sm:border-2',
    small: 'sm:size-6 sm:border-2',
    medium: 'sm:size-8 sm:border-[3px]',
    large: 'sm:size-12 sm:border-4',
    xlarge: 'sm:size-16 sm:border-4',
  },
  md: {
    xsmall: 'md:size-4 md:border-2',
    small: 'md:size-6 md:border-2',
    medium: 'md:size-8 md:border-[3px]',
    large: 'md:size-12 md:border-4',
    xlarge: 'md:size-16 md:border-4',
  },
  lg: {
    xsmall: 'lg:size-4 lg:border-2',
    small: 'lg:size-6 lg:border-2',
    medium: 'lg:size-8 lg:border-[3px]',
    large: 'lg:size-12 lg:border-4',
    xlarge: 'lg:size-16 lg:border-4',
  },
  xl: {
    xsmall: 'xl:size-4 xl:border-2',
    small: 'xl:size-6 xl:border-2',
    medium: 'xl:size-8 xl:border-[3px]',
    large: 'xl:size-12 xl:border-4',
    xlarge: 'xl:size-16 xl:border-4',
  },
} as const satisfies Record<Breakpoint, Record<SpinnerSize, string>>;

function DotsSpinner({
  size,
}: {
  readonly size: Partial<Record<Breakpoint, SpinnerSize>>;
}) {
  return (
    <div
      className={cn(
        'relative',
        getResponsiveEntries(size).map(([bp, s]) => DOTS_SIZES[bp][s]),
      )}
      aria-hidden="true"
      data-testid="ds-spinner-dots"
    >
      {Array.from({ length: 8 }).map((_, index) => (
        <div
          key={index}
          className="absolute left-1/2 top-0 w-[9.375%] h-[50%] ml-[-4.6875%] animate-fade"
          style={{
            transformOrigin: 'bottom center',
            transform: `rotate(${index * 45}deg)`,
            animationDelay: `${-1 + index * 0.125}s`,
          }}
        >
          <div className="w-full h-1/2 mt-1 rounded-full bg-current" />
        </div>
      ))}
    </div>
  );
}

function RingSpinner({
  size,
}: {
  readonly size: Partial<Record<Breakpoint, SpinnerSize>>;
}) {
  return (
    <div
      className={cn(
        'animate-spin rounded-full border-current/25 border-t-current',
        getResponsiveEntries(size).map(([bp, s]) => RING_SIZES[bp][s]),
      )}
      aria-hidden="true"
      data-testid="ds-spinner-ring"
    />
  );
}

export function Spinner({ variant = 'dots', size }: SpinnerProps) {
  const sizeConfig = resolveResponsiveProp(size, 'medium');

  if (variant === 'ring') {
    return <RingSpinner size={sizeConfig} />;
  }

  return <DotsSpinner size={sizeConfig} />;
}
